import { ContentBlockViewComponent, ContentModule } from '@tytapp/content';
import { CarouselBlock, LocalCarouselSlide } from './carousel-block';
import { Component, inject } from '@angular/core';
import { UserService } from '@tytapp/user';
import { CarouselSlidesApi } from '@tytapp/api';
import { Observable, of, shareReplay, map } from 'rxjs';
import { AppSku } from '@tytapp/core-content-blocks/block-utils';
import { environment } from '@tytapp/environment';

@Component({
    selector: 'tyt-carousel-block',
    standalone: true,
    imports: [ ContentModule ],
    template: `
        <div class="container">
            <tyt-carousel class="mobile" [changeDelay]="block.changeDelay || 15" [autoChange]="block.autoChange ?? true">
                @for (slide of carouselSlides$ | async; track slide) {
                    @if (isAudience(slide?.intended_audience) && isSkuMatch(slide.skus)) {
                        <tyt-carousel-item
                            [responsiveBackgroundImage]="slide.image"
                            [url]="slide.url"
                            />
                    }
                }
            </tyt-carousel>
            <tyt-carousel class="desktop" [changeDelay]="block.changeDelay || 15" [autoChange]="block.autoChange ?? true">
                @for (slide of carouselSlides$ | async; track slide) {
                    @if (isAudience(slide.intended_audience) && isSkuMatch(slide.skus)) {
                        <tyt-carousel-item
                            [responsiveBackgroundImage]="slide.image_desktop || slide.image"
                            [url]="slide.url"
                            />
                    }
                }
            </tyt-carousel>
        </div>
    `,
    styles: [`
        @use 'styles/constants';
        :host {
            display: block;
            container-name: component-element;
            container-type: inline-size;
        }

        .container {
            display: block;
            margin-bottom: constants.$gap-medium;
        }

        tyt-carousel {
            &.mobile {
                aspect-ratio: 1440/450;
            }

            &.desktop {
                aspect-ratio: 32/5;
            }

            width: 100%;
            //transform: scale(1.2);
            transition: 400ms opacity ease-out, 400ms scale ease-out;
        }

        @container component-element (max-width: 960px) { tyt-carousel.desktop { display: none; } }
        @container component-element (min-width: 961px) { tyt-carousel.mobile { display: none; } }

        @supports not (container-type: inline-size) {
            @media (max-width: 960px) { tyt-carousel.desktop { display: none; } }
            @media (min-width: 961px) { tyt-carousel.mobile { display: none; } }
        }

    `]
})
export class ViewComponent extends ContentBlockViewComponent<CarouselBlock> {
    private userService = inject(UserService);
    private carouselSlidesApi = inject(CarouselSlidesApi);

    homePageSlides: Observable<LocalCarouselSlide[]>;

    get carouselSlides$() {
        if (this.block.selectionMode === 'custom') {
            return of(this.block.slides as LocalCarouselSlide[]);
        } else {
            if (this.homePageSlides)
                return this.homePageSlides;

            return this.homePageSlides ??= this.carouselSlidesApi.all().pipe(
                map(slides => slides as LocalCarouselSlide[]),
                shareReplay(1)
            );
        }
    }

    get audienceType() {
        let value = this.userService.audienceType;

        // From Sept 2023 until Nov 4 2024 carousel audience types were entirely broken, showing all slides to all users.
        // Now that its fixed, perfect opportunity to drop this on the floor.

        // if (value === 'registered') {
        //     // HACK
        //     // Treat registered users as "visitor" role for now for the purposes of the
        //     // home page carousel. This must be removed in order to have unique carousel slides
        //     // for registered users vs unregistered visitors
        //     // HACK
        //     value = 'visitor';
        // }

        return value;
    }

    isAudience(intended: string[]) {
        if ((intended ?? null) === null)
            return true;
        if (intended.includes(""))
            return true; // BUG in TYT Platform 9/20/2023 when user selects "All"

        return intended.includes(this.audienceType);
    }

    isSkuMatch(skus?: AppSku[]): boolean {
        if (!skus || skus.length === 0) return true;
        return skus.includes(environment.productSku as AppSku);
    }
}
